import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { downloadFileName } from '../../api/fileResponse';
import {
  downloadProviderDocumentFileContent,
  downloadProviderDocumentRendition,
  listProviderDocumentFileMembers,
  getDocumentLoanTape,
  getProviderDocument,
  getProviderEvidenceDetails,
  getProviderEvidencePage,
  getProviderExtractedFacts,
  listProviderBatches,
  getProviderBatchItems,
  listProviderDocumentCopyContexts,
  runProviderVaAction,
} from '../../api/providerApi';
import { saveBlob } from '../../components/custodian/saveBlob';
import { FEATURES } from '../../config/features';
import {
  canMutateDigitizeVa,
  canViewDigitize,
  digitizeDeniedMessage,
} from '../../auth/digitizeAccess';
import { isReadOnlySession } from '../../auth/access';
import { readDemoSession } from '../../auth/demoSession';
import { DigitizeWorkspace } from '../../components/digitize/DigitizeWorkspace';
import { runValidateToCompletion, validateProgressMessage } from '../../digitize/validateSync';
import {
  isSyncJobQueuedResponse,
  syncJobActionLabel,
  syncJobOnLoad,
  syncJobPollOutcome,
} from '../../digitize/syncJob';
import { buttonBase, buttonVariants } from '../../components/ui/Button';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { DataTable } from '../../components/ui/DataTable';
import { isUuid } from '../../utils/format';

function vaActionErrorMessage(action, requestError) {
  const raw = requestError?.message || '';
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(raw)) {
    if (action === 'classify') {
      return (
        'The connection dropped while ABDP was still classifying. 10k–20k PDF / multi-GB batches can take hours. ' +
        'Leave this tab open — Digitize keeps checking. When Process shows Review, use Sync from ABDP.'
      );
    }
    return 'The connection dropped before the server answered. Try again, or Sync from ABDP if the batch is already in Review.';
  }
  if (raw) {
    return action === 'classify' ? `${raw} Still polling ABDP for types — leave this tab open.` : raw;
  }
  return `Unable to run ${action}.`;
}

function buildDocuments(document, facts, contexts) {
  const byId = new Map();
  if (document?.source_file_id) {
    byId.set(String(document.source_file_id), {
      id: String(document.source_file_id),
      label: document.source_file_name || 'Source file',
      fileName: document.source_file_name || undefined,
    });
  }
  (facts?.items || []).forEach((fact) => {
    if (!fact.source_file_id) return;
    const id = String(fact.source_file_id);
    if (!byId.has(id)) {
      byId.set(id, { id, label: `File ${id.slice(0, 8)}`, fileName: undefined });
    }
  });
  return Array.from(byId.values()).map((doc) => {
    const context = (contexts || []).find(
      (item) => String(item.package_file_id) === String(doc.id)
    );
    return { ...doc, copyKind: context?.copy_kind };
  });
}

export function DigitizeViewPage() {
  const { assetId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasAsset = isUuid(assetId);
  const demoSession = readDemoSession();
  const sessionRole = demoSession?.role || 'provider';
  const viewAllowed = canViewDigitize(sessionRole);
  const mutateAllowed = canMutateDigitizeVa(sessionRole) && !isReadOnlySession(demoSession);

  const [document, setDocument] = useState(null);
  const [facts, setFacts] = useState(null);
  const [loanTape, setLoanTape] = useState(undefined);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [loading, setLoading] = useState(hasAsset && viewAllowed);
  const [error, setError] = useState(
    viewAllowed ? '' : digitizeDeniedMessage(sessionRole)
  );
  const [reloadToken, setReloadToken] = useState(0);
  const [vaBusy, setVaBusy] = useState(false);
  const [vaMessage, setVaMessage] = useState('');
  const [pollUntil, setPollUntil] = useState(0);
  // Set when the last VA action was queued as a background job (large batch):
  // the poll then watches ABDP_SYNC_JOB instead of only DOCUMENT_TYPE_* facts.
  const [pollJob, setPollJob] = useState(null);
  // When the current classify was started; older ABDP_SYNC_JOB outcomes are ignored.
  const classifyStartedRef = useRef(0);
  const [copyContexts, setCopyContexts] = useState([]);

  const load = useCallback(async () => {
    if (!hasAsset || !viewAllowed) return;
    setLoading(true);
    setError('');
    try {
      const [documentResponse, factsResponse, tapeResponse] = await Promise.all([
        getProviderDocument(assetId),
        getProviderExtractedFacts(assetId),
        // 404 = no tape attached (null); other failures stay unknown (undefined).
        Promise.resolve()
          .then(() => getDocumentLoanTape(assetId))
          .catch((tapeError) => (tapeError?.status === 404 ? null : undefined)),
      ]);
      setDocument(documentResponse);
      setFacts(factsResponse);
      setLoanTape(tapeResponse);
      setSelectedDocumentId(
        documentResponse?.source_file_id
          ? String(documentResponse.source_file_id)
          : null
      );
      if (FEATURES.labelledCopies) {
        try {
          const contexts = await listProviderDocumentCopyContexts(assetId);
          setCopyContexts(Array.isArray(contexts) ? contexts : []);
        } catch {
          setCopyContexts([]);
        }
      } else {
        setCopyContexts([]);
      }
      return factsResponse;
    } catch (requestError) {
      setDocument(null);
      setFacts(null);
      setCopyContexts([]);
      setError(requestError.message || 'Unable to load document review.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [assetId, hasAsset, viewAllowed]);

  const pollJobRef = useRef(null);
  pollJobRef.current = pollJob;

  useEffect(() => {
    let cancelled = false;
    load().then((factsResponse) => {
      if (cancelled || !factsResponse || pollJobRef.current) return;
      // A background validate/extract started earlier (or in another tab)
      // must stay visible after a reload: resume polling or show the failure.
      const onLoad = syncJobOnLoad(factsResponse);
      if (onLoad.resume && mutateAllowed) {
        setPollJob(onLoad.resume);
        setPollUntil(Date.now() + 12 * 60 * 60 * 1000);
        setVaBusy(true);
        setVaMessage(onLoad.message);
      } else if (onLoad.message) {
        setVaMessage((current) => current || onLoad.message);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [load, reloadToken, mutateAllowed]);

  // Soft-poll: re-sync classify every 20s so eVault facts update when Processor finishes
  // after an HTTP timeout (finance sync classify can outlive the client read timeout).
  useEffect(() => {
    if (!hasAsset || !viewAllowed || !pollUntil) return undefined;
    if (Date.now() >= pollUntil) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (Date.now() >= pollUntil) {
        setPollUntil(0);
        setPollJob(null);
        setVaBusy(false);
        setVaMessage((msg) =>
          msg ||
          'Still waiting on ABDP types — click Run classification again to re-sync.'
        );
        return;
      }
      try {
        // Facts only — never POST classify here. Re-running classify while
        // a 500–1000 PDF zip is still creating a batch starts a second upload.
        const factsResponse = await getProviderExtractedFacts(assetId);
        if (cancelled) return;
        if (pollJob) {
          // Background job (large batch): wait for ABDP_SYNC_JOB to finish.
          const outcome = syncJobPollOutcome(factsResponse, { startedAt: pollJob.startedAt });
          setVaMessage(
            outcome.message ||
              `${pollJob.label} is running in the background — this page refreshes when it finishes.`
          );
          if (outcome.done) {
            setPollUntil(0);
            setPollJob(null);
            setVaBusy(false);
            setFacts(factsResponse);
            if (!outcome.failed) {
              setReloadToken((value) => value + 1);
              if (pollJob.action === 'extract') {
                const params = new URLSearchParams(searchParams);
                params.set('step', 'review');
                setSearchParams(params, { replace: true });
              }
            }
          }
          return;
        }
        // Post-kick follow-up: the backend re-syncs from Process every minute
        // and reports progress in ABDP_SYNC_JOB.
        const followUp = syncJobPollOutcome(factsResponse, {
          startedAt: classifyStartedRef.current,
        });
        if (followUp.failed) {
          setPollUntil(0);
          setVaBusy(false);
          setFacts(factsResponse);
          setVaMessage(followUp.message);
          return;
        }
        setVaMessage(followUp.message || 'Checking ABDP for document types…');
        if (cancelled) return;
        setFacts(factsResponse);
        const ready = (factsResponse?.items || []).some(
          (f) =>
            typeof f.fact_key === 'string' &&
            f.fact_key.startsWith('DOCUMENT_TYPE_') &&
            f.fact_value &&
            f.fact_value !== 'CLASSIFY_PENDING_OR_EMPTY'
        );
        if (ready) {
          setPollUntil(0);
          setVaBusy(false);
          setVaMessage('Classification types synced.');
          setReloadToken((value) => value + 1);
        }
      } catch {
        // ignore transient poll errors; keep trying until pollUntil
      }
    };
    const id = window.setInterval(tick, 20000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [assetId, hasAsset, viewAllowed, pollUntil, pollJob, searchParams, setSearchParams]);

  const documents = useMemo(
    () => buildDocuments(document, facts, copyContexts),
    [document, facts, copyContexts]
  );
  const activeContext =
    copyContexts.find((item) => String(item.package_file_id) === String(selectedDocumentId)) ||
    copyContexts[0] ||
    null;

  const factRows = useMemo(
    () =>
      (facts?.items || []).map((fact) => ({
        id: fact.id,
        key: fact.fact_key,
        value: fact.fact_value,
        verificationStatus: fact.verification_status || 'unverified',
        verifiedAt: fact.verified_at || null,
        verifiedByLabel: fact.verified_by_label || null,
      })),
    [facts]
  );

  const loadDocumentPreview = useCallback(
    async (doc, { member } = {}) => {
      if (!assetId || !doc?.id) {
        throw new Error('Document file is required to load the source preview.');
      }
      if (FEATURES.labelledCopies || member) {
        return downloadProviderDocumentRendition(assetId, doc.id, { disposition: 'inline', member });
      }
      return downloadProviderDocumentFileContent(assetId, doc.id);
    },
    [assetId]
  );

  // ZIP batches: list PDF members server-side so one member is fetched at a time.
  const listDocumentMembers = useCallback(
    async (doc) => {
      if (!assetId || !doc?.id) {
        throw new Error('Document file is required to list ZIP members.');
      }
      return listProviderDocumentFileMembers(assetId, doc.id);
    },
    [assetId]
  );

  async function handleDownload(doc, { member } = {}) {
    if (!assetId || !doc?.id) {
      throw new Error('Document file is required to download a review copy.');
    }
    const result = await downloadProviderDocumentRendition(assetId, doc.id, {
      disposition: 'attachment',
      member,
    });
    if (!result?.blob) {
      throw new Error('Review copy could not be loaded.');
    }
    saveBlob(
      result.blob,
      downloadFileName({
        fileName: result.fileName,
        label: doc.fileName,
        fallback: 'non-authoritative-copy',
        mimeType: result.mimeType || result.blob.type,
      })
    );
  }

  const extractEnabled = useMemo(() => {
    const items = facts?.items || [];
    const ready = items.find((f) => f.fact_key === 'ABDP_READY_FOR_EXTRACT');
    if (ready && String(ready.fact_value).toLowerCase() === 'true') return true;
    return items.some(
      (f) =>
        typeof f.fact_key === 'string' &&
        f.fact_key.startsWith('DOCUMENT_TYPE_') &&
        f.fact_value &&
        f.fact_value !== 'CLASSIFY_PENDING_OR_EMPTY'
    );
  }, [facts]);

  const runVa = useCallback(
    async (action) => {
      if (!assetId || !mutateAllowed) return false;
      setVaBusy(true);
      setVaMessage(
        action === 'classify'
          ? 'Classify is queued. Process can take hours for large batches — this page will refresh when types appear. Use Sync when Review is ready.'
          : action === 'extract'
            ? 'Running extraction… fields will appear on Review when ready.'
            : action === 'sync_review'
              ? 'Syncing Process review from ABDP… Digitize will mark fields verified when the folder is in review.'
              : ''
      );
      if (action === 'classify') {
        classifyStartedRef.current = Date.now();
        // Keep soft-polling for up to 12 minutes even if the POST times out in the browser.
        setPollUntil(Date.now() + 12 * 60 * 60 * 1000);
      }
      let queued = false;
      try {
        const result =
          action === 'validate'
            ? await runValidateToCompletion(() => runProviderVaAction(assetId, 'validate'), {
                onProgress: (page) => setVaMessage(validateProgressMessage(page)),
              })
            : await runProviderVaAction(assetId, action);
        setVaMessage(
          result?.message ||
            `${action} completed` +
              (result?.eid ? ` (eid=${result.eid})` : '')
        );
        if (isSyncJobQueuedResponse(result)) {
          // Large batch: the backend scheduled the sync; poll ABDP_SYNC_JOB.
          queued = true;
          setPollJob({
            action,
            startedAt: Date.now(),
            label: syncJobActionLabel(action),
          });
          setPollUntil(Date.now() + 12 * 60 * 60 * 1000);
          setVaBusy(true);
          return true;
        }
        setReloadToken((value) => value + 1);
        const pending =
          action === 'classify' &&
          typeof result?.message === 'string' &&
          /not ready|empty|in a minute|re-sync|could not read|auto-pipeline (running|kicked)|queued/i.test(
            result.message
          );
        if (!pending) {
          setPollUntil(0);
          setVaBusy(false);
        }
        if (action === 'extract') {
          const params = new URLSearchParams(searchParams);
          params.set('step', 'review');
          setSearchParams(params, { replace: true });
        }
        return true;
      } catch (requestError) {
        setVaMessage(vaActionErrorMessage(action, requestError));
        // Keep polling after network/timeout errors on classify.
        if (action !== 'classify') {
          setVaBusy(false);
          setPollUntil(0);
        }
        return false;
      } finally {
        if (action !== 'classify' && !queued) {
          setVaBusy(false);
        }
      }
    },
    [assetId, mutateAllowed, searchParams, setSearchParams]
  );

  if (!viewAllowed) {
    return (
      <ErrorBanner
        message={digitizeDeniedMessage(sessionRole)}
        onRetry={undefined}
      />
    );
  }

  if (!hasAsset) {
    return <AssetPickerPanel />;
  }

  return (
    <div className="space-y-4">
      <DigitizeWorkspace
        title={document?.loan_number || document?.asset_number || 'Digitize'}
        subtitle={
          document
            ? `${document.batch || '—'} · ${document.program || '—'} · ${document.borrower || '—'}`
            : undefined
        }
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((value) => value + 1)}
        documents={documents}
        selectedDocumentId={selectedDocumentId}
        onSelectDocument={setSelectedDocumentId}
        loadDocumentPreview={loadDocumentPreview}
        listDocumentMembers={listDocumentMembers}
        facts={factRows}
        validationAssetId={assetId}
        fetchValidationPage={getProviderEvidencePage}
        fetchValidationDetails={getProviderEvidenceDetails}
        validationReloadToken={reloadToken}
        canMutateVa={mutateAllowed}
        onRunClassify={mutateAllowed ? () => runVa('classify') : undefined}
        onRunExtract={mutateAllowed ? () => runVa('extract') : undefined}
        onRunValidate={mutateAllowed ? () => runVa('validate') : undefined}
        loanTape={loanTape}
        onSyncReview={mutateAllowed ? () => runVa('sync_review') : undefined}
        queueHint={[document?.batch, document?.program].filter(Boolean).join(' ')}
        vaBusy={vaBusy}
        vaMessage={vaMessage}
        extractEnabled={extractEnabled}
        extractionState={facts?.extraction_state || 'none'}
        fieldTotal={facts?.field_total || 0}
        fieldVerified={facts?.field_verified || 0}
        labelledCopies={FEATURES.labelledCopies}
        copyContext={activeContext}
        onDownloadCopy={FEATURES.labelledCopies ? handleDownload : undefined}
      />
      <div className="flex flex-wrap gap-2">
        <Link
          to="/provider/assets"
          className={`${buttonBase} ${buttonVariants.ghost}`}
        >
          ← Back to loan list
        </Link>
      </div>
    </div>
  );
}

function AssetPickerPanel() {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [items, setItems] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function loadBatches() {
      setLoadingBatches(true);
      setError('');
      try {
        const response = await listProviderBatches({
          page: 1,
          pageSize: 50,
          sortBy: 'submit_date',
          sortOrder: 'desc',
        });
        if (cancelled) return;
        const rows = response.items || [];
        setBatches(rows);
        if (rows[0]?.batch_id) {
          setSelectedBatchId(String(rows[0].batch_id));
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || 'Unable to load packages.');
        }
      } finally {
        if (!cancelled) setLoadingBatches(false);
      }
    }
    loadBatches();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  useEffect(() => {
    if (!selectedBatchId) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    async function loadItems() {
      setLoadingItems(true);
      try {
        const rows = await getProviderBatchItems(selectedBatchId);
        if (!cancelled) setItems(rows || []);
      } catch (requestError) {
        if (!cancelled) {
          setItems([]);
          setError(requestError.message || 'Unable to load package assets.');
        }
      } finally {
        if (!cancelled) setLoadingItems(false);
      }
    }
    loadItems();
    return () => {
      cancelled = true;
    };
  }, [selectedBatchId]);

  const columns = [
    {
      key: 'loan_number',
      header: 'Loan',
      render: (row) => row.loan_number || row.asset_number,
    },
    { key: 'borrower', header: 'Borrower', render: (row) => row.borrower || '—' },
    {
      key: 'authority_state',
      header: 'Authority',
      render: (row) => row.authority_state || '—',
    },
    {
      key: 'custody_state',
      header: 'Vault state',
      render: (row) => row.custody_state || '—',
    },
  ];

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
        <h2 className="text-base font-semibold text-eorig-ink">Select a loan</h2>
        <p className="mt-1 text-xs text-eorig-muted">
          Choose a package, then open a loan to review documents and extracted fields.
        </p>
        <label className="mt-4 block max-w-xl text-[12px] font-semibold text-eorig-ink">
          Package
          <select
            className="mt-1.5 w-full rounded-xl border border-eorig-border bg-white px-3 py-2 text-sm font-medium text-eorig-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
            value={selectedBatchId}
            disabled={loadingBatches}
            onChange={(event) => setSelectedBatchId(event.target.value)}
          >
            {!batches.length ? <option value="">No packages returned</option> : null}
            {batches.map((batch) => (
              <option key={batch.batch_id} value={batch.batch_id}>
                {batch.reference_number || batch.batch_id} · {batch.program}
              </option>
            ))}
          </select>
        </label>
      </section>

      {error ? (
        <ErrorBanner message={error} onRetry={() => setReloadToken((value) => value + 1)} />
      ) : null}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(row) => row.id}
        loading={loadingItems || loadingBatches}
        emptyMessage="No loans in this package yet."
        minWidth={640}
        onRowClick={(row) => navigate(`/provider/assets/${row.id}`)}
      />
    </div>
  );
}
